/**
 * ============================================================
 * stageSemantics.ts — O que cada coluna do funil SIGNIFICA
 * ============================================================
 *
 * Problema que resolve (auditoria 2026-09-14): todo o analytics inferia o
 * sentido das colunas pela POSIÇÃO — "última coluna = ganho", "segunda
 * coluna = agendamento", "3 últimas = proposta". O funil real de produção
 * tem 12 colunas e a última é **Perdido**; Desqualificado e No-show ficam
 * no meio. Resultado: Perdido contava como venda, Follow-up virava
 * "Agendamentos" e o forecast dava 95% de chance a Perdido.
 *
 * Fonte da verdade: `pipeline_columns.stage_type` (classificado pelo admin
 * na configuração da coluna). Quando a coluna ainda não foi classificada,
 * `inferStageType` deduz pelo id/label — cobre os ids reais de produção
 * (won, lost, qualified, agendamento, noshow, desqualificado, proposal,
 * negotiation, followup_ps_reunio) e os padrões PT-BR.
 *
 * Regra da casa: NENHUM módulo deve mais usar `sortedColumns[length-1]`
 * ou ids literais ('fechamento', 'proposta') para decidir ganho/perdido.
 * Use `useStageSemantics()` (hooks/useStageSemantics, React) ou
 * `buildStageSemantics()` (puro — este arquivo não importa a store, para os
 * testes rodarem sem Supabase).
 * ============================================================
 */
import type { KanbanColumnDef } from '@/store/types';

export type StageType =
  | 'open'          // topo / em trabalho (prospecção, follow-up, abordado…)
  | 'qualified'     // qualificado pelo SDR
  | 'meeting'       // reunião marcada (a agenda alimenta esta coluna)
  | 'post_meeting'  // follow-up depois da reunião
  | 'proposal'      // proposta enviada
  | 'negotiation'   // negociação
  | 'won'           // ganho → vira cliente
  | 'lost'          // perdido (terminal negativo)
  | 'disqualified'  // desqualificado (terminal negativo)
  | 'no_show';      // não compareceu (terminal negativo)

export const STAGE_TYPES: StageType[] = [
  'open', 'qualified', 'meeting', 'post_meeting', 'proposal', 'negotiation', 'won', 'lost', 'disqualified', 'no_show',
];

export const STAGE_TYPE_LABELS: Record<StageType, string> = {
  open: 'Em trabalho',
  qualified: 'Qualificado',
  meeting: 'Reunião marcada',
  post_meeting: 'Pós-reunião',
  proposal: 'Proposta',
  negotiation: 'Negociação',
  won: 'Ganho',
  lost: 'Perdido',
  disqualified: 'Desqualificado',
  no_show: 'No-show',
};

/** Terminais negativos: o lead saiu do funil sem virar cliente. */
export const NEGATIVE_STAGE_TYPES: readonly StageType[] = ['lost', 'disqualified', 'no_show'];

/**
 * Probabilidade padrão de fechamento por tipo de estágio (forecast ponderado).
 * Configurável por projeto no futuro; hoje é a tabela de referência. Terminais
 * negativos são 0 — antes o forecast dava 95% para Perdido por ser a última coluna.
 */
export const DEFAULT_STAGE_PROBABILITY: Record<StageType, number> = {
  open: 0.10,
  qualified: 0.20,
  meeting: 0.35,
  post_meeting: 0.45,
  proposal: 0.55,
  negotiation: 0.75,
  won: 1,
  lost: 0,
  disqualified: 0,
  no_show: 0,
};

const normalize = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[\s_-]+/g, ' ').trim();

/**
 * Deduz o tipo pelo id e pela label. A ordem dos testes importa:
 * "desqualificado" contém "qualificado"; "pós reunião" contém "reunião".
 */
export function inferStageType(col: Pick<KanbanColumnDef, 'id' | 'label'>): StageType {
  const text = `${normalize(col.id || '')} ${normalize(col.label || '')}`;
  if (/\bno ?show\b|nao compareceu/.test(text)) return 'no_show';
  if (/desqualif|disqualif|unqualif/.test(text)) return 'disqualified';
  if (/\b(lost|perdid[oa]s?)\b|\bperdido\b/.test(text)) return 'lost';
  if (/\b(won|ganh[oa]s?|fechad[oa]s?|fechamento|vendid[oa]|closed won)\b/.test(text)) return 'won';
  if (/pos reuni|post meeting|followup ps|follow up ps/.test(text)) return 'post_meeting';
  if (/negoc|negotiat/.test(text)) return 'negotiation';
  if (/propost|proposal/.test(text)) return 'proposal';
  if (/agend|meeting|reuni|schedul/.test(text)) return 'meeting';
  if (/qualific|qualified/.test(text)) return 'qualified';
  return 'open';
}

/** Tipo efetivo: o classificado no banco, senão a inferência. */
export function resolveStageType(col: KanbanColumnDef): StageType {
  return (col.stageType as StageType | null | undefined) || inferStageType(col);
}

export interface StageSemantics {
  /** Colunas ordenadas por `order`. */
  columns: KanbanColumnDef[];
  /** id da coluna → tipo efetivo. */
  typeOf: (columnId: string | null | undefined) => StageType;
  wonIds: Set<string>;
  lostIds: Set<string>;
  /** lost + disqualified + no_show */
  negativeIds: Set<string>;
  meetingIds: Set<string>;
  /** proposal + negotiation */
  proposalIds: Set<string>;
  qualifiedIds: Set<string>;
  /** Tudo que ainda está em aberto (nem ganho, nem terminal negativo). */
  openIds: Set<string>;
  /** Caminho principal: colunas ordenadas SEM os terminais negativos. */
  mainPath: KanbanColumnDef[];
  /** Colunas terminais negativas, ordenadas. */
  negativeColumns: KanbanColumnDef[];
  /** Primeira coluna do tipo pedido (ou null). */
  firstOf: (type: StageType) => KanbanColumnDef | null;
  isWon: (columnId: string | null | undefined) => boolean;
  isLost: (columnId: string | null | undefined) => boolean;
  isNegative: (columnId: string | null | undefined) => boolean;
  /** Ganho ou terminal negativo: o lead saiu do funil. */
  isClosed: (columnId: string | null | undefined) => boolean;
  isOpen: (columnId: string | null | undefined) => boolean;
  /** Reunião marcada ou qualquer estágio posterior no caminho principal. */
  reachedMeeting: (columnId: string | null | undefined) => boolean;
  /** Proposta/negociação ou ganho. */
  reachedProposal: (columnId: string | null | undefined) => boolean;
  /** Probabilidade de fechamento do estágio (forecast). */
  probabilityOf: (columnId: string | null | undefined) => number;
  /** Índice no caminho principal (para "avançou/regrediu"); -1 para terminais negativos. */
  mainIndexOf: (columnId: string | null | undefined) => number;
}

const MAIN_ORDER: Record<StageType, number> = {
  open: 0, qualified: 1, meeting: 2, post_meeting: 3, proposal: 4, negotiation: 5, won: 6,
  lost: -1, disqualified: -1, no_show: -1,
};

export function buildStageSemantics(kanbanColumns: KanbanColumnDef[]): StageSemantics {
  const columns = [...kanbanColumns].sort((a, b) => a.order - b.order);
  const types = new Map<string, StageType>();
  for (const c of columns) types.set(c.id, resolveStageType(c));

  const idsOf = (pred: (t: StageType) => boolean) =>
    new Set(columns.filter((c) => pred(types.get(c.id)!)).map((c) => c.id));

  const wonIds = idsOf((t) => t === 'won');
  const lostIds = idsOf((t) => t === 'lost');
  const negativeIds = idsOf((t) => NEGATIVE_STAGE_TYPES.includes(t));
  const meetingIds = idsOf((t) => t === 'meeting');
  const proposalIds = idsOf((t) => t === 'proposal' || t === 'negotiation');
  const qualifiedIds = idsOf((t) => t === 'qualified');
  const openIds = idsOf((t) => t !== 'won' && !NEGATIVE_STAGE_TYPES.includes(t));
  const mainPath = columns.filter((c) => !negativeIds.has(c.id));
  const negativeColumns = columns.filter((c) => negativeIds.has(c.id));
  const mainIndex = new Map(mainPath.map((c, i) => [c.id, i]));

  // Um id desconhecido (coluna apagada, lead antigo) é tratado como "em aberto":
  // não conta como ganho nem como perdido, e entra no funil como topo.
  const typeOf = (id: string | null | undefined): StageType => (id && types.get(id)) || 'open';

  return {
    columns,
    typeOf,
    wonIds, lostIds, negativeIds, meetingIds, proposalIds, qualifiedIds, openIds,
    mainPath, negativeColumns,
    firstOf: (type) => columns.find((c) => types.get(c.id) === type) || null,
    isWon: (id) => !!id && wonIds.has(id),
    isLost: (id) => !!id && lostIds.has(id),
    isNegative: (id) => !!id && negativeIds.has(id),
    isClosed: (id) => !!id && (wonIds.has(id) || negativeIds.has(id)),
    isOpen: (id) => !id || !(wonIds.has(id) || negativeIds.has(id)),
    reachedMeeting: (id) => MAIN_ORDER[typeOf(id)] >= MAIN_ORDER.meeting,
    reachedProposal: (id) => MAIN_ORDER[typeOf(id)] >= MAIN_ORDER.proposal,
    probabilityOf: (id) => DEFAULT_STAGE_PROBABILITY[typeOf(id)],
    mainIndexOf: (id) => (id && mainIndex.has(id) ? (mainIndex.get(id) as number) : -1),
  };
}
